import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["form", "input", "salt", "encryptedPrivateKey", "encryptedData", "template", "error", "displayArea"];

  connect() {
    // Only initialize salt buffer and input listeners if form targets exist
    if (this.hasSaltTarget) {
      this.saltBuffer = this.#encode(this.salt);
    }
    
    if (this.hasInputTarget && this.hasErrorTarget) {
      // Auto-clear errors when user starts typing
      this.inputTarget.addEventListener('input', () => {
        this.errorTarget.hidden = true;
      });
    }
  }

  async decrypt(event) {
    event.preventDefault();

    // Add loading state
    const submitButton = event.target.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Decrypting...';
    submitButton.disabled = true;

    try {
      const secretKey = await this.deriveSecretKey();
      let privateKey;
      
      privateKey = await crypto.subtle.unwrapKey(
        "jwk",
        this.#unpack(this.encryptedPrivateKey),
        secretKey,
        {
          name: "AES-GCM",
          iv: this.saltBuffer,
        },
        {
          name: "RSA-OAEP",
          hash: "SHA-256",
        },
        false,
        ["decrypt"],
      );
      
      var decryptedData = await crypto.subtle.decrypt(
        {
          name: "RSA-OAEP",
        },
        privateKey,
        this.#unpack(this.encryptedData),
      );

      const decodedString = this.#decode(decryptedData);
      const decodedData = JSON.parse(decodedString);
      this.renderCreds(decodedData);
      this.formTarget.hidden = true;
      
      // Optional: Show success message briefly
      const successDiv = document.createElement('div');
      successDiv.className = 'bg-green-50 text-green-600 px-3 py-2 rounded mb-4 text-center';
      successDiv.textContent = '✓ Credentials unlocked successfully';
      this.formTarget.parentElement.insertBefore(successDiv, this.formTarget);
      setTimeout(() => successDiv.remove(), 3000);
      
    } catch (error) {
      console.error(error);
      this.errorTarget.hidden = false;
      this.errorTarget.textContent = "❌ Incorrect passphrase. Please check and try again.";
    } finally {
      // Reset button state
      submitButton.textContent = originalText;
      submitButton.disabled = false;
    }
  }

  async deriveSecretKey() {
    const derivedKeyMaterial = await crypto.subtle.importKey(
      "raw",
      this.#encode(this.passphrase),
      { name: "PBKDF2" },
      false, // extractable
      ["deriveKey"],
    );

    return await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: this.saltBuffer,
        // https://cheatsheetseries.owasp.org/cheatsheets/Password_Storage_Cheat_Sheet.html#pbkdf2
        iterations: 600000,
        hash: "SHA-256",
      },
      derivedKeyMaterial,
      { name: "AES-GCM", length: 256 },
      true, // extractable
      ["wrapKey", "unwrapKey"],
    );
  }

  renderCreds(decodedData) {
    const clone = this.templateTarget.content.cloneNode(true);
    
    // Try to find display area target within this controller's scope
    let targetContainer = null;
    if (this.hasDisplayAreaTarget) {
      targetContainer = this.displayAreaTarget;
    } else {
      // Fallback: look for display area within this controller's element
      targetContainer = this.element.querySelector('[data-display-area]');
    }

    // Populate ALL fields with the same data attributes in the cloned template
    const loginFields = clone.querySelectorAll(`[data-login-input]`);
    const passwordFields = clone.querySelectorAll(`[data-password-input]`);
    const notesFields = clone.querySelectorAll(`[data-notes-input]`);

    loginFields.forEach(field => {
      field.textContent = decodedData.login;
    });

    passwordFields.forEach(field => {
      field.textContent = decodedData.password;
    });

    notesFields.forEach(field => {
      field.textContent = decodedData.notes;
    });

    if (targetContainer) {
      // Clear existing content and replace with unlocked content
      targetContainer.innerHTML = '';
      targetContainer.appendChild(clone);
    } else {
      this.element.append(clone);
    }
  }

  #encode(data) {
    // https://developer.mozilla.org/en-US/docs/Web/API/TextEncoder
    const encoder = new TextEncoder();

    return encoder.encode(data);
  }

  #decode(bytestream) {
    // https://developer.mozilla.org/en-US/docs/Web/API/TextDecoder
    const decoder = new TextDecoder();

    return decoder.decode(bytestream);
  }

  #unpack(packed) {
    // https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
    const string = window.atob(packed);
    const buffer = new ArrayBuffer(string.length);
    const bufferView = new Uint8Array(buffer);

    for (let i = 0; i < string.length; i++) {
      bufferView[i] = string.charCodeAt(i);
    }

    return buffer;
  }

  get salt() {
    return this.hasSaltTarget ? this.saltTarget.value : null;
  }
  get encryptedPrivateKey() {
    return this.hasEncryptedPrivateKeyTarget ? this.encryptedPrivateKeyTarget.value : null;
  }
  get encryptedData() {
    return this.hasEncryptedDataTarget ? this.encryptedDataTarget.value : null;
  }
  get passphrase() {
    return this.hasInputTarget ? this.inputTarget.value : null;
  }
}
